import React, { useState, useEffect, useRef } from "react";
import { LayoutDashboard, Users, Briefcase, CalendarDays } from "lucide-react";
import { GOLD } from "../constants/theme";
import { ONBOARDING_CSS, OB_TOUR_STEPS } from "./onboardingConstants";
import { NAV } from "../constants/navigation";
import { SIDEBAR_GROUPS } from "../constants/sidebarGroups";

const NAV_BY_ID = Object.fromEntries(NAV.map(n => [n.id, n]));

export function TourOverlay({onComplete,onNavigate}){
  const [tourStep,setTourStep]=useState(0);
  const [showLeaveModal,setShowLeaveModal]=useState(false);
  const tourData=OB_TOUR_STEPS[tourStep];
  const isLast=tourStep===OB_TOUR_STEPS.length-1;

  // Mobile detection. The desktop tour overlays a fake 240px sidebar
  // with each step's nav item highlighted — that doesn't translate to
  // phones where the real sidebar is hidden behind a drawer. Below
  // 700px we render a centered modal walkthrough instead.
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 700);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 700);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Back-button interception during tour. The tour ALSO navigates the
  // hash on every Next click (via onNavigate); we don't want THOSE
  // navigations to look like the user pressed back. ignorePopRef counts
  // pending programmatic navigations — popstate handlers decrement and
  // skip the leave modal when the count is positive.
  const ignorePopRef = useRef(0);
  useEffect(()=>{
    const pushGuard=()=>window.history.pushState(null,"",window.location.href);
    pushGuard();
    const onPop=()=>{
      if (ignorePopRef.current > 0) {
        ignorePopRef.current -= 1;
        pushGuard();
        return;
      }
      setShowLeaveModal(true);
      pushGuard();
    };
    window.addEventListener("popstate",onPop);
    const onBeforeUnload=(e)=>{e.preventDefault();e.returnValue="";};
    window.addEventListener("beforeunload",onBeforeUnload);
    return()=>{window.removeEventListener("popstate",onPop);window.removeEventListener("beforeunload",onBeforeUnload);};
  },[]);
  // The tour's own copy of the sidebar, built from the same grouping the real
  // one uses (constants/sidebarGroups.js), so the two cannot drift. Only
  // highlighted rows draw. Calls and My Portal are not sidebar rows (a tile
  // and the menu under your name); their rows sit at the foot.
  const TOUR_SIDEBAR=[
    {id:"dashboard",label:"Home",Icon:LayoutDashboard,indent:0},
    {id:"community",label:"Community",Icon:Users,indent:0},
    ...SIDEBAR_GROUPS.flatMap(g=>[
      {id:`_${g.id}`,label:g.label,Icon:g.Icon,indent:0,isHeader:true},
      ...g.items.map(([id,label])=>{const n=NAV_BY_ID[id];return n&&!n.beta&&!n.hidden?{id,label:label||n.label,Icon:n.Icon,indent:1}:null;}).filter(Boolean),
    ]),
    {id:"calls",label:"Calls",Icon:CalendarDays,indent:0},
    {id:"portal",label:"My Portal, under your name",Icon:Briefcase,indent:0},
  ];
  const GROUP_HEADER=Object.fromEntries(SIDEBAR_GROUPS.map(g=>[g.label,`_${g.id}`]));
  const highlightIds=tourData.ids;
  const activeIds=[...highlightIds];
  if(tourData.type==="group"&&GROUP_HEADER[tourData.groupLabel]) activeIds.push(GROUP_HEADER[tourData.groupLabel]);

  // Navigate to the highlighted page. Bump ignorePopRef before each
  // programmatic nav so the popstate guard (above) doesn't mistake the
  // tour's own hash change for the user pressing back.
  useEffect(()=>{
    if(onNavigate){
      ignorePopRef.current += 1;
      onNavigate(tourData.ids[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[tourStep]);

  const finish=()=>{
    if(onNavigate) onNavigate("dashboard");
    onComplete();
  };

  const advance=()=>setTourStep(t=>t+1);

  // Track which item is the last active one (for anchoring the inline info)
  const lastActiveIdx=TOUR_SIDEBAR.reduce((acc,item,i)=>activeIds.includes(item.id)?i:acc,-1);

  // ── Mobile branch: centered modal walkthrough ──────────────────────
  // The desktop tour highlights a fake 240px sidebar; on phones the real
  // sidebar is behind the drawer, so the highlight has nothing to anchor
  // against. Render a step-by-step modal with the same content instead.
  if (isMobile) {
    return (
      <div style={{position:"fixed",inset:0,zIndex:10000,fontFamily:"'DM Sans',sans-serif",pointerEvents:"auto",background:"rgba(0,0,0,0.78)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
        <style>{ONBOARDING_CSS}</style>
        {showLeaveModal && (
          <div style={{position:"fixed",inset:0,zIndex:10002,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"center",justifyContent:"center",pointerEvents:"auto",padding:20}}>
            <div style={{background:"linear-gradient(180deg,#151515,#0A0A0A)",borderRadius:16,padding:"30px 26px",maxWidth:340,width:"100%",textAlign:"center",border:"1px solid rgba(255,255,255,0.06)",boxShadow:"0 20px 60px rgba(0,0,0,0.6)"}}>
              <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:24,color:"#F5F0E8",letterSpacing:1.5,marginBottom:10}}>ARE YOU SURE?</div>
              <div style={{fontSize:13,color:"#9A9488",lineHeight:1.6,marginBottom:22}}>Your progress will not be saved.</div>
              <div style={{display:"flex",gap:10,justifyContent:"center"}}>
                <button onClick={()=>setShowLeaveModal(false)}
                  style={{padding:"12px 28px",borderRadius:10,fontSize:13,fontWeight:800,letterSpacing:1,background:GOLD,color:"#111",border:"none",fontFamily:"'Bebas Neue',sans-serif",cursor:"pointer"}}>STAY</button>
                <button onClick={()=>{setShowLeaveModal(false);finish();}}
                  style={{padding:"12px 28px",borderRadius:10,fontSize:13,fontWeight:800,letterSpacing:1,background:"transparent",color:"#9A9488",border:"1px solid rgba(255,255,255,0.10)",fontFamily:"'Bebas Neue',sans-serif",cursor:"pointer"}}>LEAVE</button>
              </div>
            </div>
          </div>
        )}
        <div className="ob-fade" key={tourStep} style={{
          background: "linear-gradient(180deg,#151515,#0E0E0E)",
          border: "1px solid rgba(226,221,159,0.18)",
          borderRadius: 18,
          padding: "26px 24px",
          maxWidth: 380,
          width: "100%",
          boxShadow: "0 24px 70px rgba(0,0,0,0.6)",
        }}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
            <span style={{fontSize:10,fontWeight:800,color:GOLD,letterSpacing:1.5,textTransform:"uppercase"}}>
              Tour · {tourStep+1} of {OB_TOUR_STEPS.length}
            </span>
          </div>
          <h2 style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:26,letterSpacing:1.8,color:"#F5F0E8",margin:"0 0 12px",lineHeight:1.1,textTransform:"uppercase"}}>
            {tourData.label}
          </h2>
          <p style={{fontSize:14,color:"#B0A898",lineHeight:1.65,margin:"0 0 24px"}}>
            {tourData.desc}
          </p>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
            <button onClick={()=>setShowLeaveModal(true)}
              style={{background:"transparent",border:"none",color:"#9A9488",fontSize:12,fontWeight:600,cursor:"pointer",padding:"6px 0",fontFamily:"'DM Sans',sans-serif"}}>
              Skip tour
            </button>
            <div style={{display:"flex",gap:4}}>
              {OB_TOUR_STEPS.map((_,i)=>(
                <div key={i} style={{width:i===tourStep?14:5,height:4,borderRadius:2,background:i<=tourStep?GOLD:"#444",transition:"all .25s ease"}}/>
              ))}
            </div>
            <button className="ob-btn" onClick={isLast?finish:advance}
              style={{padding:"10px 20px",borderRadius:8,fontSize:13,fontWeight:800,letterSpacing:.5,background:GOLD,color:"#111",border:"none",fontFamily:"'DM Sans',sans-serif",cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
              {isLast ? "Done" : <>Next {"→"}</>}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{position:"fixed",inset:0,zIndex:10000,fontFamily:"'DM Sans',sans-serif",pointerEvents:"none"}}>
      <style>{ONBOARDING_CSS}</style>
      {/* Prompt 9: Leave modal during tour */}
      {showLeaveModal&&(
        <div style={{position:"fixed",inset:0,zIndex:10002,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"center",justifyContent:"center",pointerEvents:"auto"}}>
          <div style={{background:"linear-gradient(180deg,#151515,#0A0A0A)",borderRadius:16,padding:"36px 40px",maxWidth:400,textAlign:"center",border:"1px solid rgba(255,255,255,0.06)",boxShadow:"0 20px 60px rgba(0,0,0,0.6)"}}>
            <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:26,color:"#F5F0E8",letterSpacing:1.5,marginBottom:12}}>ARE YOU SURE?</div>
            <div style={{fontSize:14,color:"#9A9488",lineHeight:1.7,marginBottom:28}}>Your progress will not be saved.</div>
            <div style={{display:"flex",gap:12,justifyContent:"center"}}>
              <button onClick={()=>setShowLeaveModal(false)}
                style={{padding:"12px 32px",borderRadius:10,fontSize:14,fontWeight:800,letterSpacing:1,
                  background:GOLD,color:"#111",border:"none",fontFamily:"'Bebas Neue',sans-serif",cursor:"pointer"}}>STAY</button>
              <button onClick={()=>{setShowLeaveModal(false);finish();}}
                style={{padding:"12px 32px",borderRadius:10,fontSize:14,fontWeight:800,letterSpacing:1,
                  background:"transparent",color:"#9A9488",border:"1px solid rgba(255,255,255,0.10)",fontFamily:"'Bebas Neue',sans-serif",cursor:"pointer"}}>LEAVE</button>
            </div>
          </div>
        </div>
      )}
      {/* Backdrop covering the main content area (right of the sidebar) */}
      <div style={{position:"absolute",top:0,left:240,right:0,bottom:0,background:"rgba(0,0,0,0.72)",pointerEvents:"auto"}}/>
      {/* Sidebar highlight layer — opaque background so the real sidebar
          doesn't bleed through and create double-text artifacts. The
          panel renders the tour's own version of the nav with the
          relevant item highlighted; everything else is empty space. */}
      <div style={{position:"absolute",top:0,left:0,width:240,bottom:0,pointerEvents:"auto",
        background:"linear-gradient(180deg, #0E0E0E 0%, #0A0A0A 100%)",
        borderRight:"1px solid rgba(255,255,255,0.04)"}}>
        <div style={{position:"relative",height:"100%",display:"flex",flexDirection:"column"}}>
          <div style={{height:66,flexShrink:0}}/>
          <div style={{flex:1,padding:"10px 0",overflowY:"auto"}}>
            {TOUR_SIDEBAR.map((item,idx)=>{
              const isActive=activeIds.includes(item.id);
              const isHeader=item.isHeader;
              const isLastActive=idx===lastActiveIdx;
              if(!isActive) return <div key={item.id} style={{padding:item.indent?"7px 18px 7px 44px":`${isHeader?"10px":"9px"} 18px`,fontSize:isHeader?10:item.indent?12.5:13}}/>;
              return (
                <div key={item.id}>
                  <div style={{position:"relative",
                    padding:item.indent?"7px 18px 7px 44px":`${isHeader?"10px":"9px"} 18px`,
                    fontSize:isHeader?10:item.indent?12.5:13,
                    fontWeight:700,
                    color:isHeader?GOLD:"#F2F2F2",
                    background:isHeader?"rgba(226,221,159,0.08)":"rgba(226,221,159,0.12)",
                    borderLeft:isHeader?"3px solid transparent":`3px solid ${GOLD}`,
                    display:"flex",alignItems:"center",gap:isHeader?11:item.indent?10:11,
                    letterSpacing:isHeader?.8:0,textTransform:isHeader?"uppercase":"none",
                    borderRadius:6,margin:"0 6px"}}>
                    <item.Icon size={isHeader?15:item.indent?14:16} color={GOLD} strokeWidth={2.2}/>
                    {item.label}
                    <div className="ob-tour-ring" style={{position:"absolute",inset:-2,borderRadius:8,border:`2px solid ${GOLD}`,pointerEvents:"none"}}/>
                  </div>
                  {/* Inline info anchored directly below the last highlighted item */}
                  {isLastActive&&(
                    <div className="ob-fade" key={tourStep} style={{padding:"10px 18px 6px",pointerEvents:"auto"}}>
                      <div style={{fontSize:12,color:"#B0A898",lineHeight:1.5,marginBottom:10}}>{tourData.desc}</div>
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        {isLast
                          ?<button className="ob-btn" onClick={finish}
                            style={{padding:"6px 18px",borderRadius:7,fontSize:12,fontWeight:800,letterSpacing:1,
                              background:GOLD,color:"#111",border:"none",fontFamily:"'Bebas Neue',sans-serif",cursor:"pointer"}}>
                            DONE
                          </button>
                          :<button className="ob-btn" onClick={advance}
                            style={{padding:"6px 16px",borderRadius:7,fontSize:12,fontWeight:700,
                              background:"transparent",color:GOLD,border:`1px solid ${GOLD}`,
                              fontFamily:"'DM Sans',sans-serif",cursor:"pointer"}}>
                            Next {"\u2192"}
                          </button>
                        }
                        <div style={{display:"flex",gap:3}}>
                          {OB_TOUR_STEPS.map((_,i)=>(
                            <div key={i} style={{width:i===tourStep?14:4,height:3,borderRadius:2,
                              background:i<=tourStep?GOLD:"#555",transition:"all .3s"}}/>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
